import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { ChatProject } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  project: ChatProject;
  onEdit: () => void;
}

export function ChatProjectMenu({ project, onEdit }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const deleteMutation = useMutation({
    mutationFn: () => api.chatProjectDelete(project.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'projects'] });
      qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right + 4 });
    }
    setOpen((v) => !v);
  };

  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="프로젝트 메뉴"
        onClick={toggle}
        className="size-7 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-strong hover:text-foreground"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: pos.top, left: pos.left }}
              className="w-44 z-50 rounded-md border border-border bg-surface shadow-lg py-1 text-sm"
            >
              <MenuItem
                icon={Pencil}
                onClick={() => {
                  close();
                  onEdit();
                }}
              >
                수정
              </MenuItem>
              <div className="my-1 border-t border-border" />
              <MenuItem
                icon={Trash2}
                danger
                onClick={() => {
                  close();
                  const msg =
                    project.sessionCount > 0
                      ? `프로젝트 "${project.name}" 를 삭제할까요?\n안의 채팅 ${project.sessionCount}개는 미분류로 풀려납니다.`
                      : `프로젝트 "${project.name}" 를 삭제할까요?`;
                  if (window.confirm(msg)) deleteMutation.mutate();
                }}
              >
                삭제
              </MenuItem>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  icon: Icon,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm hover:bg-surface-muted',
        danger ? 'text-destructive' : 'text-foreground',
      )}
    >
      <Icon className="size-4 shrink-0" />
      <span>{children}</span>
    </button>
  );
}
